/**
 * Criteria Editor Component
 * 
 * Displays extracted TOR criteria grouped by category.
 * Users can toggle on/off, edit labels, add custom criteria, and remove.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    TextField,
    IconButton,
    Stack,
    Tooltip,
    Button
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { MatrixCriterion } from '../services/skillsMatrix';

interface CriteriaEditorProps {
    criteria: MatrixCriterion[];
    onChange: (updated: MatrixCriterion[]) => void;
}

/** Category display config */
const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
    thematic: { label: 'Thematic / Sector', color: '#7c4dff' },
    donor: { label: 'Donor Experience', color: '#ff6d00' },
    geographic: { label: 'Geographic', color: '#00bfa5' },
    methods: { label: 'Methods / Tools', color: '#2979ff' },
    languages: { label: 'Languages', color: '#00e676' },
    education: { label: 'Education', color: '#f50057' },
    experience: { label: 'Experience', color: '#ff9100' },
    custom: { label: 'Custom', color: '#78909c' },
};

const CriteriaEditor: React.FC<CriteriaEditorProps> = ({ criteria, onChange }) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [addingCategory, setAddingCategory] = useState<string | null>(null);
    const [newLabel, setNewLabel] = useState('');

    // Group criteria by category
    const grouped = criteria.reduce<Record<string, MatrixCriterion[]>>((acc, c) => {
        const cat = c.category || 'custom';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(c);
        return acc;
    }, {});

    // Ordered categories
    const categoryOrder = ['thematic', 'donor', 'geographic', 'methods', 'languages', 'education', 'experience', 'custom'];
    const orderedCategories = categoryOrder.filter(cat => grouped[cat]?.length > 0);

    const handleToggle = (id: string) => {
        onChange(criteria.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));
    };

    const handleStartEdit = (c: MatrixCriterion) => {
        setEditingId(c.id);
        setEditValue(c.label);
    };

    const handleSaveEdit = () => {
        if (editingId && editValue.trim()) {
            onChange(criteria.map(c => {
                if (c.id !== editingId) return c;
                const newLabel = editValue.trim().toLowerCase();
                const existingTerms = c.search_terms.filter(t => t !== c.label.toLowerCase());
                const updatedTerms = [newLabel, ...existingTerms];
                return { ...c, label: editValue.trim(), search_terms: Array.from(new Set(updatedTerms)) };
            }));
        }
        setEditingId(null);
        setEditValue('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleDelete = (id: string) => {
        onChange(criteria.filter(c => c.id !== id));
    };

    const handleAdd = (category: string) => {
        if (!newLabel.trim()) return;
        const newId = `custom_${Date.now()}`;
        const newCriterion: MatrixCriterion = {
            id: newId,
            label: newLabel.trim(),
            category: category as MatrixCriterion['category'],
            search_terms: [newLabel.trim().toLowerCase()],
            enabled: true
        };
        onChange([...criteria, newCriterion]);
        setNewLabel('');
        setAddingCategory(null);
    };

    const handleToggleAll = (category: string, enable: boolean) => {
        onChange(criteria.map(c => c.category === category ? { ...c, enabled: enable } : c));
    };

    return (
        <Box>
            {orderedCategories.map(cat => {
                const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.custom;
                const items = grouped[cat];
                const enabledCount = items.filter(c => c.enabled).length;

                return (
                    <Box key={cat} sx={{ mb: 2.5 }}>
                        {/* Category header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box
                                sx={{
                                    width: 4,
                                    height: 20,
                                    borderRadius: 1,
                                    bgcolor: config.color
                                }}
                            />
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: config.color }}>
                                {config.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({enabledCount}/{items.length} active)
                            </Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Enable all">
                                    <Chip
                                        label="All on"
                                        size="small"
                                        variant={enabledCount === items.length ? 'filled' : 'outlined'}
                                        onClick={() => handleToggleAll(cat, true)}
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Disable all">
                                    <Chip
                                        label="All off"
                                        size="small"
                                        variant={enabledCount === 0 ? 'filled' : 'outlined'}
                                        onClick={() => handleToggleAll(cat, false)}
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Criteria items */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
                            {items.map(c => (
                                <Box key={c.id}>
                                    {editingId === c.id ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TextField
                                                size="small"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit();
                                                    if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                                autoFocus
                                                sx={{ width: 200 }}
                                            />
                                            <IconButton size="small" onClick={handleSaveEdit} color="primary">
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={handleCancelEdit}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ) : (
                                        <Chip
                                            label={c.label}
                                            variant={c.enabled ? 'filled' : 'outlined'}
                                            onClick={() => handleToggle(c.id)}
                                            onDelete={() => handleDelete(c.id)}
                                            deleteIcon={
                                                <Tooltip title="Remove">
                                                    <DeleteIcon fontSize="small" />
                                                </Tooltip>
                                            }
                                            sx={{
                                                bgcolor: c.enabled ? `${config.color}20` : 'transparent',
                                                borderColor: c.enabled ? config.color : 'rgba(255,255,255,0.2)',
                                                color: c.enabled ? 'white' : 'rgba(255,255,255,0.4)',
                                                '&:hover': {
                                                    bgcolor: `${config.color}30`
                                                },
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgba(255,255,255,0.3)',
                                                    '&:hover': { color: '#ef5350' }
                                                }
                                            }}
                                            icon={
                                                <Tooltip title="Edit label">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleStartEdit(c); }}
                                                        sx={{ p: 0, ml: 0.5 }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        />
                                    )}
                                </Box>
                            ))}

                            {/* Add button for this category */}
                            {addingCategory === cat ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <TextField
                                        size="small"
                                        placeholder="New criterion..."
                                        value={newLabel}
                                        onChange={(e) => setNewLabel(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAdd(cat);
                                            if (e.key === 'Escape') { setAddingCategory(null); setNewLabel(''); }
                                        }}
                                        autoFocus
                                        sx={{ width: 200 }}
                                    />
                                    <IconButton size="small" onClick={() => handleAdd(cat)} color="primary">
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => { setAddingCategory(null); setNewLabel(''); }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ) : (
                                <Chip
                                    label="+ Add"
                                    variant="outlined"
                                    onClick={() => { setAddingCategory(cat); setNewLabel(''); }}
                                    sx={{
                                        borderStyle: 'dashed',
                                        borderColor: 'rgba(255,255,255,0.2)',
                                        color: 'rgba(255,255,255,0.4)',
                                        '&:hover': { borderColor: config.color, color: config.color }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                );
            })}

            {/* Add custom category */}
            {!orderedCategories.includes('custom') && (
                <Box sx={{ mt: 2, pl: 2 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setAddingCategory('custom');
                            setNewLabel('');
                        }}
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                        Add Custom Criterion
                    </Button>
                    {addingCategory === 'custom' && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                            <TextField
                                size="small"
                                placeholder="Custom criterion label..."
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAdd('custom');
                                    if (e.key === 'Escape') { setAddingCategory(null); setNewLabel(''); }
                                }}
                                autoFocus
                                sx={{ width: 250 }}
                            />
                            <IconButton size="small" onClick={() => handleAdd('custom')} color="primary">
                                <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => { setAddingCategory(null); setNewLabel(''); }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CriteriaEditor;
